import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAccountReadContext } from '@app/context/AccountContextContext'

import { useNavigate } from '@lib/react-router-dom'

import { IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { HamburgerIcon } from '@ui/icons/Hamburger.icon'

import { isAccountDetailedActivated } from '@model/AccountDetailedDto'

import { TMEntityLogoFactory } from '@assets/logo/TickmillLogoIcons'

import { MenuLink } from '../../MenuLink'
import { useHideSensitiveData } from '../../menuSections/topLevelMenus/useHideSensitiveData'
import { useLanguageMenu } from '../../menuSections/topLevelMenus/useLanguageMenu'
import { useMinimalMenuItems } from '../hooks/useMinimalMenuItems'
import { LanguagePicker } from './LanguagePicker'

import styles from './TopBar.module.scss'

export const TopBarCondensed: React.FC = () => {
  const navigate = useNavigate()
  const languageMenu = useLanguageMenu()
  const hideSensitiveData = useHideSensitiveData()
  const minimalMenuItems = useMinimalMenuItems()
  const { account } = useAccountReadContext()

  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  const toggleProfile = useCallback(() => setIsProfileOpen((prev) => !prev), [])
  const shouldShowSensitiveDataToggle = isAccountDetailedActivated(account)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsProfileOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const handleItemClick = useCallback(
    (item: (typeof minimalMenuItems)[number]) => {
      if (item.onClick) {
        item.onClick()
      } else if (item.path) {
        navigate(item.path)
      }
      setIsProfileOpen(false)
    },
    [navigate]
  )

  const dropdownItems = minimalMenuItems.filter(
    (item) => !item.id.includes('language') && !item.id.includes('hide-sensitive')
  )

  return (
    <header className={classNames(styles.topBar, styles.topBarMinimal)}>
      <Link to='/'>
        <div className={styles.logo}>
          <TMEntityLogoFactory />
        </div>
      </Link>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        {shouldShowSensitiveDataToggle && hideSensitiveData.icon && (
          <IconButton.Ghost
            icon={hideSensitiveData.icon}
            size='md'
            onClick={hideSensitiveData.onClick}
            aria-label={hideSensitiveData.label}
          />
        )}

        <LanguagePicker languageMenu={languageMenu} />

        <div ref={profileRef} className={styles.profileContainer}>
          <IconButton.Ghost
            icon={HamburgerIcon}
            size='md'
            onClick={toggleProfile}
            aria-expanded={isProfileOpen}
          />

          {isProfileOpen && (
            <div className={styles.profileDropdown}>
              {dropdownItems.map((item) => (
                <MenuLink
                  key={item.id}
                  section={item}
                  onClick={() => handleItemClick(item)}
                  className={styles.profileDropdownItem}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
